import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Upload, Image as ImageIcon, X, Loader2, Gift, Copy, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CONTACT, buildWhatsappMessage, SHAPE_PLACEHOLDERS } from "@/lib/products";
import { trackWhatsAppClick } from "@/lib/analytics";

export default function ConfiguratorDialog({ product, open, onOpenChange, initialPhotoFile, initialText }) {
  const [step, setStep] = useState(1);
  const [selections, setSelections] = useState({});
  const [texts, setTexts] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "" });
  const [giftPackaging, setGiftPackaging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState(null);

  const reset = () => {
    setStep(1);
    setSelections({});
    setTexts({});
    setPhotoFile(null);
    setPhotoPreview("");
    setCustomer({ name: "", phone: "", address: "" });
    setGiftPackaging(false);
    setSubmitting(false);
    setSubmittedOrder(null);
  };

  // Prefill state when a new product is opened from the Preview section
  useEffect(() => {
    if (!product) return;
    // reset first, then apply initial values
    setStep(1);
    setSelections({});
    setCustomer({ name: "", phone: "", address: "" });
    setGiftPackaging(false);
    setSubmitting(false);
    setSubmittedOrder(null);

    if (initialPhotoFile) {
      setPhotoFile(initialPhotoFile);
      const url = URL.createObjectURL(initialPhotoFile);
      setPhotoPreview(url);
    } else {
      setPhotoFile(null);
      setPhotoPreview("");
    }

    if (initialText) {
      const map = {
        "silver-photo-coin-30g": "messageLine1",
        "silver-photo-coin-51mm": "frameText",
        "silver-photo-coin-10g": "frameText",
        "silver-photo-coin-5g-float": "coinBackText",
        "arched-multi-coin-momento": "baseMessage",
        "gold-locket-engraved-10g": "engravingText",
        "silver-plate-momento-100g": "frontMessage",
        "silver-square-photo-coin-tombstone": "frameText",
        "silver-photo-coin-100g-grand-tombstone": "frameText",
        "gold-nameplate-stretch-bracelet": "nameOrInitials",
      };
      const key = map[product.id];
      setTexts(key ? { [key]: initialText } : {});
    } else {
      setTexts({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  const close = (openState) => {
    if (!openState) reset();
    onOpenChange(openState);
  };

  const optionKeys = useMemo(() => (product ? Object.keys(product.options) : []), [product]);
  const fieldKeys = useMemo(() => (product ? Object.keys(product.fields || {}) : []), [product]);

  if (!product) return null;

  const setOpt = (k, v) => setSelections((s) => ({ ...s, [k]: v }));
  const setTxt = (k, v) => setTexts((s) => ({ ...s, [k]: v }));

  const handlePhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please upload an image file.");
      return;
    }
    if (f.size > 12 * 1024 * 1024) {
      toast.error("Image is too large (max 12MB).");
      return;
    }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const nextStep = () => {
    // Validate step 1
    if (step === 1) {
      for (const k of optionKeys) {
        if (!selections[k]) {
          toast.error(`Please choose ${labelise(k)}`);
          return;
        }
      }
    }
    if (step === 2) {
      // Validate texts
      for (const k of fieldKeys) {
        if (k === "photo") continue;
        const def = product.fields[k];
        const val = (texts[k] || "").trim();
        if (!val) {
          toast.error(`Please enter ${def.label.toLowerCase()}`);
          return;
        }
        if (def.maxWords && val.split(/\s+/).length > def.maxWords) {
          toast.error(`${def.label} exceeds ${def.maxWords} words`);
          return;
        }
        if (def.maxChars && val.length > def.maxChars) {
          toast.error(`${def.label} exceeds ${def.maxChars} characters`);
          return;
        }
      }
      if (product.fields.photo && !photoFile) {
        // Allow skip when Style = Initials
        if (product.id === "locket" && selections.style === "Initials") {
          // ok
        } else {
          toast.error("Please upload a photo to engrave.");
          return;
        }
      }
    }
    setStep(step + 1);
  };

  const submit = async () => {
    if (!customer.name.trim() || !customer.phone.trim() || !customer.address.trim()) {
      toast.error("Please fill in your name, phone number and delivery address.");
      return;
    }
    setSubmitting(true);
    const customization = { ...selections, ...texts };
    try {
      // 1. Upload the engraving photo (if any) so it's attached to the saved order.
      let photo_path = null;
      if (photoFile) {
        const form = new FormData();
        form.append("file", photoFile);
        const up = await api.post("/uploads", form);
        photo_path = up.data.path;
      }

      // 2. Save the order — this also auto-notifies the business owner by email
      // and drives the Admin dashboard's Orders tab.
      const res = await api.post("/orders", {
        product_type: product.productType,
        product_name: product.name,
        customization,
        photo_path,
        customer_name: customer.name.trim(),
        customer_phone: customer.phone.trim(),
        customer_address: customer.address.trim(),
        gift_packaging: giftPackaging,
        referred_by: localStorage.getItem("soc_ref") || null,
      });
      const order = res.data;

      // 3. Auto-fill a WhatsApp message to the business owner with every detail,
      // and open it — the customer only needs to tap Send once.
      const waUrl = `${CONTACT.whatsapp}?text=${encodeURIComponent(buildWhatsappMessage(order))}`;
      trackWhatsAppClick({ placement: "configurator_submit", productId: product.id });
      toast.success("Order saved — opening WhatsApp to notify us…");
      window.open(waUrl, "_blank");
      localStorage.removeItem("soc_ref");
      setSubmittedOrder(order);
    } catch (e) {
      console.error(e);
      // Fallback: even if saving to our system failed, still get every detail
      // to the business owner on WhatsApp so the order isn't lost.
      const custLines = Object.entries(customization)
        .filter(([, v]) => v && String(v).trim().length)
        .map(([k, v]) => `• ${labelise(k)}: ${v}`);
      const msgLines = [
        `*New Custom Order — ${product.name}*`,
        "",
        ...custLines,
        "",
        `*Name:* ${customer.name}`,
        `*Phone:* ${customer.phone}`,
        `*Address:* ${customer.address}`,
      ];
      if (giftPackaging) msgLines.push(`*Gift Wrapping:* Yes`);
      if (photoFile) msgLines.push("", "_I'll share the photograph to engrave in this chat._");
      const waUrl = `${CONTACT.whatsapp}?text=${encodeURIComponent(msgLines.join("\n"))}`;
      trackWhatsAppClick({ placement: "configurator_submit_fallback", productId: product.id });
      window.open(waUrl, "_blank");
      toast.error("Couldn't save your order, but we've opened WhatsApp with your details — please tap Send.");
      setSubmittedOrder({
        id: `SOC-${Date.now().toString(36).toUpperCase()}`,
        product_name: product.name,
        customer_name: customer.name.trim(),
        gift_packaging: giftPackaging,
        status: "received",
        referrer_code: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-w-5xl w-[95vw] max-h-[92vh] p-0 border-white/10 bg-transparent overflow-hidden"
        data-testid="configurator-dialog"
      >
        <div className="grid md:grid-cols-5 rounded-2xl overflow-hidden glass-strong max-h-[92vh]">
          {/* Left preview */}
          <div className="md:col-span-2 relative bg-black/60 border-r border-white/5 shrink-0">
            <div className="relative aspect-[16/9] md:aspect-auto md:h-full">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-3 left-4 right-4 md:bottom-6 md:left-6 md:right-6">
                <div className="text-[10px] uppercase tracking-widest-2 text-[#d4af37]">{product.tag}</div>
                <DialogTitle className="mt-1 font-display text-xl md:text-4xl text-white leading-tight">{product.name}</DialogTitle>
                <DialogDescription className="hidden md:block text-white/60 mt-2 text-sm md:text-base">{product.blurb}</DialogDescription>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="md:col-span-3 p-5 md:p-10 max-h-[calc(92vh-56vw)] md:max-h-[92vh] overflow-y-auto">
            <DialogHeader className="text-left mb-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest-2 text-[#d4af37]">
                Step {step} of 3
              </div>
              <div className="mt-3 flex gap-2">
                {[1, 2, 3].map((s) => (
                  <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? "bg-[#d4af37]" : "bg-white/10"}`} />
                ))}
              </div>
            </DialogHeader>

            {step === 1 && (
              <div className="space-y-6">
                <h3 className="font-display text-2xl text-white">Choose your options</h3>
                {optionKeys.map((k) => (
                  <div key={k}>
                    <Label className="text-xs uppercase tracking-widest-2 text-white/60">{labelise(k)}</Label>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {product.options[k].map((v) => (
                        <button
                          key={v}
                          onClick={() => setOpt(k, v)}
                          className={`px-4 py-2 rounded-full border text-sm transition ${
                            selections[k] === v
                              ? "bg-[#d4af37] text-black border-[#d4af37]"
                              : "border-white/15 text-white/80 hover:border-[#d4af37]/60 hover:text-white"
                          }`}
                          data-testid={`option-${k}-${v.toLowerCase().replace(/\s/g, "-")}`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>

                    {/* Shape preview */}
                    {k === "shape" && selections.shape && (
                      <div className="mt-4 flex items-center gap-4">
                        <div className="w-20 h-20 rounded-2xl overflow-hidden glass border border-white/10">
                          <img
                            src={SHAPE_PLACEHOLDERS[selections.shape.toLowerCase()] || product.image}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="text-xs text-white/50">
                          Reference image — your piece will be crafted in the
                          <span className="text-white/80"> {selections.metal || "chosen metal"}</span>.
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <div className="pt-4 flex justify-end">
                  <Button
                    onClick={nextStep}
                    className="rounded-full bg-[#d4af37] text-black hover:brightness-110"
                    data-testid="configurator-next-step-1"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <h3 className="font-display text-2xl text-white">Personalize it</h3>

                {product.fields.photo && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest-2 text-white/60">
                      {product.id === "locket" && selections.style === "Initials"
                        ? "Photo (optional — initials selected)"
                        : "Upload photograph"}
                    </Label>
                    <label className="mt-3 relative flex items-center gap-4 rounded-2xl border border-dashed border-white/15 hover:border-[#d4af37]/60 p-4 cursor-pointer transition bg-white/[0.02]">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhoto}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        data-testid="configurator-photo-input"
                      />
                      {photoPreview ? (
                        <div className="relative">
                          <img src={photoPreview} alt="preview" className="w-20 h-20 rounded-xl object-cover" />
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); setPhotoFile(null); setPhotoPreview(""); }}
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black/80 border border-white/20 text-white flex items-center justify-center"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-xl bg-white/5 flex items-center justify-center">
                          <ImageIcon className="text-white/40" />
                        </div>
                      )}
                      <div>
                        <div className="text-white font-medium flex items-center gap-2">
                          <Upload size={14} className="text-[#d4af37]" /> {photoFile ? photoFile.name.slice(0, 40) : "Click to upload"}
                        </div>
                        <div className="text-xs text-white/50 mt-1">JPG, PNG or WEBP — up to 12MB</div>
                      </div>
                    </label>
                  </div>
                )}

                {fieldKeys.filter((k) => k !== "photo").map((k) => {
                  const def = product.fields[k];
                  const val = texts[k] || "";
                  const wordCount = val.trim() ? val.trim().split(/\s+/).length : 0;
                  return (
                    <div key={k}>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs uppercase tracking-widest-2 text-white/60">{def.label}</Label>
                        <span className="text-[10px] text-white/40">
                          {def.maxWords ? `${wordCount}/${def.maxWords} words` : ""}
                          {def.maxChars ? `${val.length}/${def.maxChars}` : ""}
                        </span>
                      </div>
                      {def.maxWords && def.maxWords > 4 ? (
                        <Textarea
                          value={val}
                          onChange={(e) => setTxt(k, e.target.value)}
                          rows={2}
                          className="mt-3 bg-white/5 border-white/10 focus:border-[#d4af37] text-white"
                          placeholder="e.g. Best Wishes on 50th Anniversary"
                          data-testid={`configurator-text-${k}`}
                        />
                      ) : (
                        <Input
                          value={val}
                          onChange={(e) => setTxt(k, e.target.value)}
                          maxLength={def.maxChars || 60}
                          className="mt-3 bg-white/5 border-white/10 focus:border-[#d4af37] text-white"
                          placeholder={def.maxChars ? "e.g. S" : "e.g. Best Wishes"}
                          data-testid={`configurator-text-${k}`}
                        />
                      )}
                    </div>
                  );
                })}

                <div className="pt-4 flex items-center justify-between">
                  <Button
                    variant="ghost"
                    onClick={() => setStep(1)}
                    className="text-white/60 hover:text-white"
                    data-testid="configurator-back-step-2"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={nextStep}
                    className="rounded-full bg-[#d4af37] text-black hover:brightness-110"
                    data-testid="configurator-next-step-2"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && !submittedOrder && (
              <div className="space-y-6">
                <h3 className="font-display text-2xl text-white">Review &amp; send on WhatsApp</h3>
                <p className="text-white/60 text-sm -mt-4">
                  We only take orders through WhatsApp. Fill in your delivery details, tap the button
                  below, and we'll open a chat with everything pre-filled — just tap Send.
                </p>

                <div className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest-2 text-white/60">Your Name</Label>
                    <Input
                      value={customer.name}
                      onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
                      className="mt-2 bg-white/5 border-white/10 focus:border-[#d4af37] text-white"
                      placeholder="e.g. Priya Sharma"
                      data-testid="configurator-customer-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest-2 text-white/60">Phone Number</Label>
                    <Input
                      value={customer.phone}
                      onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))}
                      className="mt-2 bg-white/5 border-white/10 focus:border-[#d4af37] text-white"
                      placeholder="e.g. 98765 43210"
                      data-testid="configurator-customer-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest-2 text-white/60">Delivery Address</Label>
                    <Textarea
                      value={customer.address}
                      onChange={(e) => setCustomer((c) => ({ ...c, address: e.target.value }))}
                      rows={2}
                      className="mt-2 bg-white/5 border-white/10 focus:border-[#d4af37] text-white"
                      placeholder="House no, street, area, city, pincode"
                      data-testid="configurator-customer-address"
                    />
                  </div>
                </div>

                <SummaryCard product={product} selections={selections} texts={texts} photoPreview={photoPreview} />

                {/* Gift packaging toggle */}
                <div className="rounded-2xl glass border border-white/10 p-4 flex items-start gap-4">
                  <button
                    type="button"
                    onClick={() => setGiftPackaging((g) => !g)}
                    className={`relative shrink-0 w-12 h-7 rounded-full transition ${giftPackaging ? "bg-[#d4af37]" : "bg-white/10"}`}
                    aria-pressed={giftPackaging}
                    data-testid="gift-packaging-toggle"
                  >
                    <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-black transition-transform ${giftPackaging ? "translate-x-5" : ""}`} />
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Gift size={16} className="text-[#d4af37]" />
                      <span className="font-medium text-white">Add Premium Gift Wrapping</span>
                    </div>
                    <div className="text-xs text-white/55 mt-1">
                      Your order will be beautifully packed in a premium gift box, ready to present.
                    </div>
                  </div>
                </div>

                <div className="pt-2 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => setStep(2)}
                    className="text-white/60 hover:text-white w-full sm:w-auto"
                    data-testid="configurator-back-step-3"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={submit}
                    disabled={submitting}
                    className="rounded-full bg-[#25D366] text-black hover:brightness-110 h-12 px-6 w-full sm:w-auto sm:min-w-[220px] font-medium"
                    data-testid="configurator-submit"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Opening…</span>
                    ) : (
                      "Order via WhatsApp"
                    )}
                  </Button>
                </div>
              </div>
            )}

            {submittedOrder && (
              <OrderConfirmation order={submittedOrder} onClose={() => close(false)} />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const labelise = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

const SummaryCard = ({ product, selections, texts, photoPreview }) => (
  <div className="glass rounded-2xl p-5 border border-white/5">
    <div className="text-xs uppercase tracking-widest-2 text-[#d4af37]">Order Summary</div>
    <div className="mt-3 flex gap-4 items-start">
      {photoPreview && (
        <img src={photoPreview} alt="preview" className="w-16 h-16 rounded-lg object-cover" />
      )}
      <div className="flex-1">
        <div className="font-display text-lg text-white">{product.name}</div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/70">
          {Object.entries(selections).map(([k, v]) => (
            <span key={k}><span className="text-white/40 uppercase text-[10px] mr-1">{labelise(k)}:</span>{v}</span>
          ))}
        </div>
        {Object.entries(texts).map(([k, v]) => v && (
          <div key={k} className="mt-2 text-sm text-white/80">
            <span className="text-white/40 uppercase text-[10px] mr-1">{labelise(k)}:</span>"{v}"
          </div>
        ))}
      </div>
    </div>
  </div>
);

const OrderConfirmation = ({ order, onClose }) => {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/?ref=${order.referrer_code}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };
  const shareMsg = `I just ordered a personalised keepsake from Shine on Coin. Use my link for your order too: ${link}`;
  const waShare = `https://wa.me/?text=${encodeURIComponent(shareMsg)}`;
  return (
    <div className="space-y-6" data-testid="order-confirmation">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-full bg-gradient-to-br from-[#f3e5ab] via-[#d4af37] to-[#7a5d10] text-black flex items-center justify-center">
          <Check size={22} />
        </span>
        <div>
          <div className="text-xs uppercase tracking-widest-2 text-[#d4af37]">Order confirmed</div>
          <div className="font-display text-2xl text-white">Thank you, {order.customer_name.split(" ")[0]}!</div>
        </div>
      </div>
      <p className="text-white/70">
        Your order for <span className="text-white">{order.product_name}</span> has been received.
        We've opened WhatsApp with the details — reply there to finalise your quote.
      </p>
      <div className="glass rounded-2xl p-5 space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-white/50">Order ID</span><span className="text-white font-mono">{order.id.slice(0,8)}</span></div>
        {order.gift_packaging && <div className="flex justify-between"><span className="text-white/50">Gift wrapping</span><span className="text-[#d4af37]">Included</span></div>}
        <div className="flex justify-between"><span className="text-white/50">Status</span><span className="text-white capitalize">{order.status.replace('_',' ')}</span></div>
      </div>
      <div className="rounded-2xl border border-[#d4af37]/40 p-5 bg-[#d4af37]/5">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest-2 text-[#d4af37]">
          <Share2 size={14} /> Refer a friend
        </div>
        <div className="mt-2 font-display text-xl text-white">Share this link — earn our thanks.</div>
        <p className="text-sm text-white/60 mt-1">
          When a friend orders through your link, we'll credit the referral to your name in our studio ledger.
        </p>
        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 min-w-0 rounded-full bg-black/50 border border-white/10 px-4 py-2 text-xs md:text-sm text-white/80 truncate" data-testid="referral-link">
            {link}
          </div>
          <button onClick={copy} className="rounded-full bg-white text-black h-9 px-4 text-xs font-medium hover:brightness-95 inline-flex items-center gap-1" data-testid="referral-copy">
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <a
          href={waShare}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 rounded-full glass border border-white/10 px-4 py-2 text-xs text-white/80 hover:border-[#d4af37]"
          data-testid="referral-share-whatsapp"
        >
          <Share2 size={14} /> Share on WhatsApp
        </a>
      </div>
      <div className="flex justify-end">
        <Button onClick={onClose} className="rounded-full bg-[#d4af37] text-black hover:brightness-110" data-testid="order-confirmation-close">
          Done
        </Button>
      </div>
    </div>
  );
};

